/**
 * "You're near the Main Stage — now: X · next: Y at 22:00" (plan.md step 60).
 *
 * **Computed on the phone, and nowhere else.** Stage positions are drawn on the
 * map image (x/y fractions); the surveyed control points fit a warp from GPS to
 * the image (`fitTps`), and this fits the INVERSE — image to GPS — so a stage's
 * map pin becomes a place. A festival with fewer than three control points has
 * no warp, so no proximity, and says so (`stagePlaces` → null).
 *
 * Once per arrival: standing at a stage notifies once; a stage you came back
 * to within the cooldown stays quiet; quiet hours and a daily cap hold the rest.
 */
import { fitTps, geoToImage, haversineMetres, type ControlPoint } from './geo.js';
import { zonedParts } from './time.js';

export interface StagePlace { stageId: string; name: string; lat: number; lng: number }

/** Image fractions are 0..1; degrees across a site are ~0.005 — bring them to one scale before fitting. */
const IMAGE_TO_FIT = 0.01;

/** Image position → GPS, through the inverse of the site's warp. Null with fewer than three control points. */
export function imageToGeoFit(controls: readonly ControlPoint[]): ((x: number, y: number) => { lat: number; lng: number }) | null {
  if (controls.length < 3) return null;
  let fit;
  try {
    fit = fitTps(controls.map((c) => ({ lng: c.x * IMAGE_TO_FIT, lat: c.y * IMAGE_TO_FIT, x: c.lat, y: c.lng })));
  } catch {
    return null;
  }
  return (x, y) => {
    const p = geoToImage(fit, y * IMAGE_TO_FIT, x * IMAGE_TO_FIT);
    return { lat: p.x, lng: p.y };
  };
}

/** Each stage with a map pin, as a place on earth — or null when the site has no warp. */
export function stagePlaces(
  map: { points: readonly { id: string; x: number; y: number }[]; controlPoints: readonly ControlPoint[] } | undefined,
  stages: readonly { id: string; name: string; mapPointId?: string | null }[],
): StagePlace[] | null {
  if (!map) return null;
  const toGeo = imageToGeoFit(map.controlPoints);
  if (!toGeo) return null;
  const points = new Map(map.points.map((p) => [p.id, p]));
  const out: StagePlace[] = [];
  for (const s of stages) {
    const p = s.mapPointId ? points.get(s.mapPointId) : undefined;
    if (p) out.push({ stageId: s.id, name: s.name, ...toGeo(p.x, p.y) });
  }
  return out;
}

export function nearestStage(places: readonly StagePlace[], here: { lat: number; lng: number }, radiusM: number): { place: StagePlace; metres: number } | null {
  let best: { place: StagePlace; metres: number } | null = null;
  for (const place of places) {
    const metres = haversineMetres(here.lat, here.lng, place.lat, place.lng);
    if (metres <= radiusM && (!best || metres < best.metres)) best = { place, metres };
  }
  return best;
}

export const PROXIMITY_RADII = [25, 50, 100, 250] as const;

export interface ProximitySettings {
  radiusM: number;
  /** No repeat for a stage within this, even after walking away and back. */
  cooldownMs: number;
  /** Wall clock in the festival's zone, `HH:MM`; quiet from → to (may wrap midnight). */
  quietFrom?: string | null;
  quietTo?: string | null;
  /** The zone quiet hours are in — the person's own; the festival's when absent. */
  quietTimeZone?: string | null;
  dailyCap: number;
}

export const DEFAULT_PROXIMITY: ProximitySettings = { radiusM: 50, cooldownMs: 90 * 60_000, quietFrom: null, quietTo: null, dailyCap: 12 };

export interface ProximityState {
  /** The stage you are at now, or null between stages. */
  at: string | null;
  /** When each stage last notified, epoch ms. */
  told: Record<string, number>;
  /** The festival day (zone) the cap counts, and how many so far. */
  day: string | null;
  count: number;
}

export const EMPTY_PROXIMITY: ProximityState = { at: null, told: {}, day: null, count: 0 };

const minutesOf = (hhmm: string) => { const [h, m] = hhmm.split(':').map(Number); return (h ?? 0) * 60 + (m ?? 0); };

export function inQuietHours(now: Date, timeZone: string, from?: string | null, to?: string | null): boolean {
  if (!from || !to) return false;
  const p = zonedParts(now, timeZone);
  const t = p.hour * 60 + p.minute;
  const a = minutesOf(from);
  const b = minutesOf(to);
  return a <= b ? t >= a && t < b : t >= a || t < b;
}

/** Arriving at a stage notifies once; staying, coming back soon, quiet hours and the cap do not. */
export function proximityDecision(
  state: ProximityState, near: StagePlace | null, now: Date, settings: ProximitySettings, timeZone: string,
): { notify: StagePlace | null; state: ProximityState } {
  if (!near) return { notify: null, state: { ...state, at: null } };
  if (near.stageId === state.at) return { notify: null, state };
  const p = zonedParts(now, timeZone);
  const day = `${p.year}-${p.month}-${p.day}`;
  const count = state.day === day ? state.count : 0;
  const arrived = { ...state, at: near.stageId, day, count };
  const last = state.told[near.stageId];
  if (last !== undefined && now.getTime() - last < settings.cooldownMs) return { notify: null, state: arrived };
  if (inQuietHours(now, settings.quietTimeZone ?? timeZone, settings.quietFrom, settings.quietTo)) return { notify: null, state: arrived };
  if (count >= settings.dailyCap) return { notify: null, state: arrived };
  return { notify: near, state: { ...arrived, told: { ...state.told, [near.stageId]: now.getTime() }, count: count + 1 } };
}
